import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, ArrowRight, Eye, EyeOff, User, Building, Phone, KeyRound, ChevronLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SignIn = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ emailOrId: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [googleLoading, setGoogleLoading] = useState(false);
  const googleProcessed = useRef(false);

  // Reset password form state
  const [showReset, setShowReset] = useState(false);
  const [resetForm, setResetForm] = useState({ mobile: '', newPassword: '', confirmPassword: '' });
  const [showResetPw, setShowResetPw] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState('');

  // ── EMERGENT GOOGLE AUTH CALLBACK HANDLER ──
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
  // THIS BREAKS THE AUTH. The redirect uses window.location.origin only.
  useEffect(() => {
    const hash = window.location.hash || '';
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) return;
    if (googleProcessed.current) return;
    googleProcessed.current = true; // synchronous guard — prevents StrictMode double-run

    const sessionId = decodeURIComponent(match[1]);
    // Clear the hash from the URL immediately so a refresh doesn't retry it
    window.history.replaceState(null, '', window.location.pathname + window.location.search);

    setGoogleLoading(true);
    (async () => {
      try {
        const resp = await axios.post(`${API}/crm/auth/google/exchange`, { session_id: sessionId });
        const { token, user } = resp.data;
        localStorage.setItem('crm_token', token);
        localStorage.setItem('crm_user', JSON.stringify(user));
        localStorage.setItem('crm_login_at', new Date().toISOString());
        toast.success(`Welcome, ${user.firstName}!`);
        navigate('/dashboard/main');
      } catch (err) {
        const detail = err.response?.data?.detail || '';
        const isNotLinked = detail.toLowerCase().includes('not linked') || detail.toLowerCase().includes('not registered');
        if (isNotLinked) {
          setError(
            'GOOGLE_SSO_FAILED: You signed in with a Google account that is not registered in the DBK CRM. ' +
            'Please use EMAIL + PASSWORD login below instead.'
          );
        } else {
          setError(detail || 'Google sign-in failed. Please use email + password login below.');
        }
        toast.error('Google sign-in failed. Please use email + password instead.');
      } finally {
        setGoogleLoading(false);
      }
    })();
  }, [navigate]);

  const handleGoogleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
    // THIS BREAKS THE AUTH.
    const redirectUrl = window.location.origin + '/signin';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const response = await axios.post(`${API}/crm/signin`, formData);
      const { token, user } = response.data;
      localStorage.setItem('crm_token', token);
      localStorage.setItem('crm_user', JSON.stringify(user));
      localStorage.setItem('crm_login_at', new Date().toISOString());
      toast.success(`Welcome back, ${user.firstName}!`);
      navigate('/dashboard/main');
    } catch (err) {
      const message = err.response?.data?.detail || 'Sign in failed. Please check your credentials.';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleResetChange = (e) => {
    setResetForm({ ...resetForm, [e.target.name]: e.target.value });
    setResetError('');
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    if (resetForm.newPassword !== resetForm.confirmPassword) {
      setResetError('Passwords do not match');
      return;
    }
    if (resetForm.newPassword.length < 6) {
      setResetError('Password must be at least 6 characters');
      return;
    }
    setResetLoading(true);
    setResetError('');
    try {
      await axios.post(`${API}/crm/reset-password`, {
        mobile: resetForm.mobile,
        newPassword: resetForm.newPassword,
        confirmPassword: resetForm.confirmPassword,
      });
      toast.success('Password reset successfully! Please sign in with your new password.');
      setShowReset(false);
      setResetForm({ mobile: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      const message = err.response?.data?.detail || 'Reset failed. Please check your mobile number.';
      setResetError(message);
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div data-testid="signin-page" className="min-h-screen pt-20">
      <section
        className="relative min-h-[calc(100vh-80px)] flex items-center justify-center py-20 px-6"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1622015663319-e97e697503ee?crop=entropy&cs=srgb&fm=jpg&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-stone-900/70" />

        <div className="relative z-10 w-full max-w-md">
          <div className="bg-white p-8 shadow-2xl">

            {/* ── RESET PASSWORD VIEW ── */}
            {showReset ? (
              <>
                <div className="text-center mb-6">
                  <div className="w-14 h-14 bg-emerald-800 rounded-full flex items-center justify-center mx-auto mb-3">
                    <KeyRound className="w-7 h-7 text-white" />
                  </div>
                  <h1 className="text-2xl font-playfair font-bold text-stone-900 mb-1">Reset Password</h1>
                  <p className="text-stone-500 text-sm">Enter your registered mobile number and a new password</p>
                </div>

                <form onSubmit={handleResetSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1.5">Mobile Number</label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                      <Input
                        type="tel"
                        name="mobile"
                        placeholder="Enter your registered mobile number"
                        value={resetForm.mobile}
                        onChange={handleResetChange}
                        required
                        data-testid="reset-mobile-input"
                        className="pl-10 h-12 border-stone-300 focus:ring-1 focus:ring-emerald-900 focus:border-emerald-900"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1.5">New Password</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                      <Input
                        type={showResetPw ? 'text' : 'password'}
                        name="newPassword"
                        placeholder="Enter new password (min 6 characters)"
                        value={resetForm.newPassword}
                        onChange={handleResetChange}
                        required
                        data-testid="reset-new-password-input"
                        className="pl-10 pr-10 h-12 border-stone-300 focus:ring-1 focus:ring-emerald-900 focus:border-emerald-900"
                      />
                      <button
                        type="button"
                        onClick={() => setShowResetPw(!showResetPw)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                      >
                        {showResetPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1.5">Confirm New Password</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                      <Input
                        type={showResetPw ? 'text' : 'password'}
                        name="confirmPassword"
                        placeholder="Confirm new password"
                        value={resetForm.confirmPassword}
                        onChange={handleResetChange}
                        required
                        data-testid="reset-confirm-password-input"
                        className="pl-10 h-12 border-stone-300 focus:ring-1 focus:ring-emerald-900 focus:border-emerald-900"
                      />
                    </div>
                  </div>

                  {resetError && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                      {resetError}
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={resetLoading}
                    data-testid="reset-submit-btn"
                    className="w-full bg-emerald-900 hover:bg-emerald-800 text-white h-12 uppercase tracking-widest text-xs font-bold"
                  >
                    {resetLoading ? 'RESETTING...' : 'RESET PASSWORD'}
                  </Button>

                  <button
                    type="button"
                    onClick={() => { setShowReset(false); setResetError(''); }}
                    className="w-full flex items-center justify-center gap-2 text-sm text-stone-500 hover:text-stone-700 mt-2"
                    data-testid="back-to-signin-btn"
                  >
                    <ChevronLeft className="w-4 h-4" /> Back to Sign In
                  </button>
                </form>
              </>
            ) : (

            /* ── SIGN IN VIEW ── */
            <>
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-emerald-900 rounded-full flex items-center justify-center mx-auto mb-4">
                  <User className="w-8 h-8 text-white" />
                </div>
                <h1 className="text-3xl font-playfair font-bold text-stone-900 mb-2">Welcome Back</h1>
                <p className="text-stone-600">Sign in to your DBK Group account</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Email / Employee ID / Mobile Number
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                    <Input
                      type="text"
                      name="emailOrId"
                      placeholder="Email, Employee ID or Mobile Number"
                      value={formData.emailOrId}
                      onChange={handleChange}
                      required
                      data-testid="signin-email-input"
                      className="pl-10 h-12 border-stone-300 focus:ring-1 focus:ring-emerald-900 focus:border-emerald-900"
                    />
                  </div>
                  <p className="text-xs text-stone-400 mt-1">You can use your email, employee ID, or 10-digit mobile number</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      placeholder="Enter your password"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      data-testid="signin-password-input"
                      className="pl-10 pr-10 h-12 border-stone-300 focus:ring-1 focus:ring-emerald-900 focus:border-emerald-900"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" className="rounded border-stone-300 text-emerald-900 focus:ring-emerald-900" />
                    <span className="text-sm text-stone-600">Remember me</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => { setShowReset(true); setError(''); }}
                    data-testid="forgot-password-btn"
                    className="text-sm text-emerald-900 hover:text-emerald-800 font-medium"
                  >
                    Forgot password?
                  </button>
                </div>

                {error && (
                  <div className={`p-3 rounded text-sm border ${error.startsWith('GOOGLE_SSO_FAILED:') ? 'bg-amber-50 border-amber-300 text-amber-800' : 'bg-red-50 border-red-200 text-red-700'}`}
                    data-testid="signin-error-box">
                    {error.startsWith('GOOGLE_SSO_FAILED:') ? (
                      <div>
                        <p className="font-semibold mb-1">Google account not recognized</p>
                        <p className="text-xs mb-2">
                          The Google account you used is not registered in DBK CRM.
                          <strong> Please use your professional email and password below.</strong>
                        </p>
                        <p className="text-xs bg-amber-100 rounded px-2 py-1 font-mono">
                          Email: yourname@siddhivinayakinfra.estate<br/>
                          Password: Contact HR for your password
                        </p>
                      </div>
                    ) : (
                      error
                    )}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={loading || googleLoading}
                  data-testid="signin-submit-btn"
                  className="w-full bg-emerald-900 hover:bg-emerald-800 text-white h-12 uppercase tracking-widest text-xs font-bold transition-all duration-300"
                >
                  {loading ? 'SIGNING IN...' : (
                    <>SIGN IN <ArrowRight className="ml-2 w-4 h-4" /></>
                  )}
                </Button>
              </form>

              {/* ── GOOGLE WORKSPACE SSO ── */}
              <div className="mt-4 relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-stone-200"></div>
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-3 text-stone-400 tracking-wider">or</span>
                </div>
              </div>

              <button
                type="button"
                onClick={handleGoogleSignIn}
                disabled={loading || googleLoading}
                data-testid="google-signin-btn"
                className="mt-4 w-full flex items-center justify-center gap-3 h-12 border border-stone-300 rounded bg-white hover:bg-stone-50 disabled:opacity-60 transition-colors font-medium text-stone-700 text-sm"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
                  <path fill="#4285F4" d="M22.5 12.27c0-.79-.07-1.54-.2-2.27H12v4.51h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.32z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.83z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83C6.71 7.31 9.14 5.38 12 5.38z"/>
                </svg>
                {googleLoading ? 'Signing in with Google…' : 'Sign in with Google Workspace'}
              </button>

              <p className="mt-1 text-[11px] text-stone-400 text-center px-2">
                Only works with your <strong>@siddhivinayakinfra.estate</strong> Google Workspace account.<br/>
                If it fails, use <strong>Email + Password</strong> above instead.
              </p>

              {/* Mobile login hint */}
              <div className="mt-5 p-3 bg-sky-50 border border-sky-200 rounded-lg flex items-start gap-2">
                <Phone className="w-4 h-4 text-sky-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-sky-700">
                  <strong>Mobile Login:</strong> Enter your 10-digit mobile number instead of email to sign in.
                </p>
              </div>

              <div className="mt-6 pt-6 border-t border-stone-200 text-center">
                <p className="text-stone-600">
                  Don't have an account?{' '}
                  <Link to="/signup" className="text-emerald-900 hover:text-emerald-800 font-bold">Sign Up</Link>
                </p>
              </div>

              <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <Building className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">Admin Access</p>
                    <p className="text-xs text-amber-700 mt-1">
                      For admin panel access, <Link to="/admin/login" className="underline font-medium">click here</Link>
                    </p>
                  </div>
                </div>
              </div>
            </>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default SignIn;
